import { supabase } from "@/integrations/supabase/client";

interface UploadOptions {
  bucket: string;
  path?: string;
  thresholdWidth?: number;
  targetWidth?: number;
  keepOriginal?: boolean;
}

/**
 * Upload an image to Supabase storage with optional resizing.
 * If the image width exceeds thresholdWidth, it will be resized to targetWidth.
 * Returns the public URL of the uploaded image.
 */
export async function uploadResizedImage(
  file: File,
  options: UploadOptions
): Promise<string> {
  const {
    bucket,
    path = "",
    thresholdWidth = 200,
    targetWidth = 150,
    keepOriginal = true,
  } = options;

  const fileExt = file.name.split(".").pop() || "jpg";
  const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
  const fullPath = path ? `${path}${fileName}` : fileName;

  const img = await createImageBitmap(file);

  if (img.width > thresholdWidth) {
    // Create resized version
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d")!;
    const scaleFactor = targetWidth / img.width;
    canvas.width = targetWidth;
    canvas.height = img.height * scaleFactor;
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise<Blob>((resolve) => {
      canvas.toBlob((blob) => resolve(blob!), file.type);
    });

    // Upload original for backup if requested
    if (keepOriginal) {
      await supabase.storage.from(bucket).upload(fullPath, file);
    }

    // Upload resized version
    const smallFileName = fileName.replace(/\.(\w+)$/, "-small.$1");
    const smallPath = path ? `${path}${smallFileName}` : smallFileName;
    const { error: uploadError } = await supabase.storage
      .from(bucket)
      .upload(smallPath, blob);

    if (uploadError) throw uploadError;

    const { data } = supabase.storage.from(bucket).getPublicUrl(smallPath);
    return data.publicUrl;
  } else {
    // Original is small enough, upload as-is
    const { error: uploadError } = await supabase.storage
      .from(bucket)
      .upload(fullPath, file);

    if (uploadError) throw uploadError;

    const { data } = supabase.storage.from(bucket).getPublicUrl(fullPath);
    return data.publicUrl;
  }
}
